/**
 * Утилита для извлечения человекочитаемого сообщения об ошибке
 * из различных типов ошибок (Error, Axios, строка, unknown).
 *
 * Решает проблему P0 из frontend readiness review:
 * обработка ошибок через `error instanceof Error ? error.message : ...`
 * не покрывала серверные ошибки с полезной нагрузкой.
 */

/**
 * Извлекает сообщение об ошибке из любого типа ошибки.
 *
 * Поддерживаемые форматы:
 * - `Error` — возвращает `error.message`
 * - Axios error с `response.data.message` — возвращает серверное сообщение
 * - Axios error с `response.data.error` — возвращает серверное сообщение
 * - Строка — возвращает как есть
 * - `null`/`undefined` — возвращает fallback
 *
 * @param error — объект ошибки любого типа
 * @param fallback — сообщение по умолчанию, если не удалось извлечь
 * @returns человекочитаемое сообщение об ошибке
 */
export function getErrorMessage(
  error: unknown,
  fallback: string = 'Произошла ошибка. Попробуйте ещё раз.'
): string {
  // Строка — возвращаем как есть
  if (typeof error === 'string') {
    return error;
  }

  // null/undefined — возвращаем fallback
  if (error == null) {
    return fallback;
  }

  // Объект с response (Axios-подобная ошибка)
  if (typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string; error?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
    if (response?.data?.error) {
      return response.data.error;
    }
  }

  // Объект с message (Error-подобная ошибка)
  if (typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: string }).message;
    if (message && typeof message === 'string') {
      return message;
    }
  }

  return fallback;
}
